import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

interface DiscountCardProps {
  onClose: () => void;
}

export const DiscountCard = ({ onClose }: DiscountCardProps) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Prevent scrolling when modal is open
    document.body.style.overflow = 'hidden';

    // Force scroll to top of page immediately
    window.scrollTo(0, 0);

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleCloseClick = () => {
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate submission
    setTimeout(() => {
      alert('Discount code sent to: ' + email);
      setIsSubmitting(false);
      onClose();
    }, 1500);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1001] flex flex-col justify-start items-center sm:pt-8 pt-2 px-4 pointer-events-auto overflow-y-auto"
      style={{
        backdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, y: -20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: -20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className="flex flex-col md:flex-row max-w-4xl w-full md:max-h-[600px] rounded-xl overflow-hidden shadow-2xl relative my-4"
        style={{
          boxShadow: '0 0 40px rgba(59, 130, 246, 0.4)',
        }}
      >
        {/* Left Panel - Form */}
        <div className="bg-gradient-to-br from-white to-white/95 text-black w-full md:w-1/2 p-8 space-y-6 relative">
          <motion.button
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            onClick={handleCloseClick}
            aria-label="Close"
            whileHover={{ scale: 1.1, rotate: 90 }}
            transition={{ duration: 0.2 }}
          >
            <CloseIcon className="w-6 h-6" />
          </motion.button>

          <div className="space-y-6">
            <motion.h2
              className="text-xl font-bold text-center uppercase tracking-wide"
              style={{
                background: 'linear-gradient(to right, #1e40af, #60a5fa)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Limited Time Offer
            </motion.h2>

            <div className="text-center space-y-2">
              <motion.p
                className="text-6xl font-extrabold"
                style={{
                  background: 'linear-gradient(to right, #1e40af, #3b82f6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                25% OFF
              </motion.p>
              <motion.p
                className="font-semibold text-gray-700"
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                Thank you for being one of our valued shoppers!
              </motion.p>
            </div>

            <motion.div
              className="text-center font-medium text-gray-700 border-t border-b border-gray-200 py-3"
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              You&apos;ve unlocked an exclusive discount coupon!
            </motion.div>

            <motion.form
              onSubmit={handleSubmit}
              className="flex flex-col items-center space-y-4"
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <label className="sr-only" htmlFor="email">
                Enter your email address
              </label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address to claim"
                className="w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg px-4 py-3"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white font-medium py-3 rounded-lg transition-all transform hover:scale-[1.02] duration-300 shadow-md hover:shadow-blue-500/30"
              >
                {isSubmitting ? 'Processing...' : 'Continue'}
              </Button>
              <Button
                type="button"
                className="text-sm text-gray-600 hover:text-blue-700 font-medium"
                variant="ghost"
                onClick={handleCloseClick}
              >
                I don&apos;t want a discount
              </Button>
            </motion.form>
          </div>
        </div>

        {/* Right Panel - Image */}
        <div className="w-full md:w-1/2 h-[300px] md:h-auto relative">
          <Image
            alt="Tudor Lady-Sub Watch"
            className="w-full h-full object-cover"
            src="/lady-sub.jpg"
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none"></div>

          {/* Decorative elements */}
          <motion.div
            className="absolute bottom-10 left-10 text-white text-2xl font-bold"
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <motion.div
              className="absolute -inset-4 rounded-full bg-blue-500/20 blur-xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                repeatType: 'reverse',
              }}
            />
            <div className="relative">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">
                Limited Offer
              </span>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

function CloseIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
